import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Key as KeyIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { WorkspaceAdmin } from '../components/WorkspaceAdmin';

const API_URL = process.env.REACT_APP_API_URL || '';
const MIN_PASSWORD_LENGTH = 12;

interface TeamUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user' | 'demo';
  isActive: boolean;
  createdAt: string;
  lastLogin: string | null;
  isSelf: boolean;
  /** Their home workspace — Users lists everyone across the workspaces you belong to */
  workspace?: string | null;
  inCurrentWorkspace?: boolean;
}

const formatDate = (stamp: string | null) => {
  if (!stamp) return 'Never';
  const iso = stamp.includes('T') ? stamp : stamp.replace(' ', 'T') + 'Z';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return stamp;
  return date.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

export const Users: React.FC = () => {
  const { token } = useAuth();
  const theme = useTheme();
  // Form dialogs get the full screen on a phone; a centred box leaves no room to type.
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));

  const [users, setUsers] = useState<TeamUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  // Default to the lower privilege level; admin has to be chosen deliberately
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'user' });
  const [saving, setSaving] = useState(false);

  const [resetFor, setResetFor] = useState<TeamUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetRequests, setResetRequests] = useState<{ id: string; userId: string; email: string; name: string; requestedAt: string }[]>([]);

  const loadUsers = useCallback(async () => {
    try {
      // Pending reset requests ride along with the roster
      fetch(`${API_URL}/api/password-reset-requests`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
        .then(r => (r.ok ? r.json() : []))
        .then(setResetRequests)
        .catch(() => setResetRequests([]));

      const response = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        setUsers(await response.json());
      } else if (response.status === 403) {
        setForbidden(true);
      } else {
        setError('Failed to load users');
      }
    } catch (err) {
      setError('Network error loading users');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleAdd = async () => {
    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`${data.email} added — they share this team's connections and posts.`);
        setAddOpen(false);
        setForm({ email: '', name: '', password: '', role: 'user' });
        loadUsers();
      } else {
        setError(data.error || 'Failed to add user');
      }
    } catch (err) {
      setError('Network error adding user');
    } finally {
      setSaving(false);
    }
  };

  const patchUser = async (user: TeamUser, body: object, description: string) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/users/${user.id}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(description);
        loadUsers();
        return true;
      }
      setError(data.error || 'Update failed');
      return false;
    } catch (err) {
      setError('Network error updating user');
      return false;
    }
  };

  const dismissRequest = async (id: string) => {
    try {
      await fetch(`${API_URL}/api/password-reset-requests/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      loadUsers();
    } catch (err) {
      setError('Network error dismissing the request');
    }
  };

  const handleResetPassword = async () => {
    if (!resetFor) return;
    const ok = await patchUser(resetFor, { password: newPassword }, `Password reset for ${resetFor.email}`);
    if (ok) {
      setResetFor(null);
      setNewPassword('');
    }
  };

  const handleDelete = async (user: TeamUser) => {
    if (!window.confirm(
      `Remove ${user.email}?\n\nTheir posts and connections stay with the team. Their API keys are revoked.`
    )) return;

    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/users/${user.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();

      if (response.ok) {
        setSuccess(`${user.email} removed`);
        loadUsers();
      } else {
        setError(data.error || 'Failed to remove user');
      }
    } catch (err) {
      setError('Network error removing user');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (forbidden) {
    return (
      <Box>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>Users</Typography>
        <Alert severity="info">Only admins can manage users.</Alert>
      </Box>
    );
  }

  const passwordTooShort = form.password.length > 0 && form.password.length < MIN_PASSWORD_LENGTH;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center', mb: 1, gap: 2 }}>
        <Typography variant="h4" component="h1">Users</Typography>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setAddOpen(true)}>
          Add user
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Everyone in every workspace you belong to, with the workspace each person works in. People in
        the same workspace share its platform connections, posts, scheduled queue and history —
        anything one publishes is visible to the rest. New people are added to the workspace you are in.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {resetRequests.map(request => {
        const user = users.find(u => u.id === request.userId);
        return (
          <Alert
            key={request.id}
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                {user && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => { setResetFor(user); setNewPassword(''); }}
                  >
                    Reset password
                  </Button>
                )}
                <Button size="small" onClick={() => dismissRequest(request.id)}>
                  Dismiss
                </Button>
              </Box>
            }
          >
            <strong>{request.name}</strong> ({request.email}) requested a password reset
            {' '}on {formatDate(request.requestedAt)}.
          </Alert>
        );
      })}

      <Card>
        <CardContent>
          {/* Six columns do not fit a phone. Scrolls inside the card so the page body never
              scrolls sideways — the name column stays put as the row scrolls under it. */}
          <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Workspace</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last login</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} sx={{ opacity: user.isActive ? 1 : 0.55 }}>
                  <TableCell>
                    {user.name}
                    {user.isSelf && (
                      <Chip label="you" size="small" variant="outlined" sx={{ ml: 1, height: 18 }} />
                    )}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color={user.inCurrentWorkspace ? 'text.primary' : 'text.secondary'}>
                      {user.workspace || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" variant="standard">
                      <Select
                        value={user.role}
                        onChange={(e) => patchUser(user, { role: e.target.value }, `${user.email} is now ${e.target.value}`)}
                        disableUnderline
                        sx={{ fontSize: '0.875rem' }}
                      >
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="user">User</MenuItem>
                        <MenuItem value="demo">Demo</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? 'Active' : 'Disabled'}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                      variant={user.isActive ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(user.lastLogin)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Reset password">
                      <IconButton size="small" onClick={() => { setResetFor(user); setNewPassword(''); }}>
                        <KeyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!user.isSelf && (
                      <>
                        <Tooltip title={user.isActive ? 'Disable access' : 'Re-enable access'}>
                          <IconButton
                            size="small"
                            onClick={() => patchUser(
                              user,
                              { isActive: !user.isActive },
                              `${user.email} ${user.isActive ? 'disabled' : 're-enabled'}`
                            )}
                          >
                            {user.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove from team">
                          <IconButton size="small" onClick={() => handleDelete(user)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Who exists is one question; which workspace they may work in is another. Both are
          admin-only and both belong on this page. */}
      <WorkspaceAdmin />

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth fullScreen={narrow}>
        <DialogTitle>Add a user</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>They will share your connected accounts</AlertTitle>
            This person will be able to publish to every platform this team has connected,
            and will see all posts and history. Only add people you trust with those accounts.
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              size="small"
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              fullWidth
              size="small"
            />
            <TextField
              label="Initial password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              fullWidth
              size="small"
              error={passwordTooShort}
              helperText={
                passwordTooShort
                  ? `At least ${MIN_PASSWORD_LENGTH} characters`
                  : 'Share this with them securely — they can change it after signing in.'
              }
            />
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={form.role}
                label="Role"
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <MenuItem value="admin">Admin — can also manage users and API keys</MenuItem>
                <MenuItem value="user">User — can post, but not manage users</MenuItem>
                <MenuItem value="demo">Demo — LinkedIn only, cannot change connections</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={
              saving ||
              !form.name.trim() ||
              !form.email.includes('@') ||
              form.password.length < MIN_PASSWORD_LENGTH
            }
          >
            {saving ? 'Adding…' : 'Add user'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(resetFor)} onClose={() => setResetFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Setting a new password for {resetFor?.email}. Any active session of theirs ends.
          </Typography>
          <TextField
            label="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            size="small"
            autoFocus
            error={newPassword.length > 0 && newPassword.length < MIN_PASSWORD_LENGTH}
            helperText={`At least ${MIN_PASSWORD_LENGTH} characters`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetFor(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleResetPassword}
            disabled={newPassword.length < MIN_PASSWORD_LENGTH}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
